import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useTerm } from '../../contexts/TermContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { BarChart3, Plus, Download, FileText, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import { successToast } from '../../lib/successToast';
import { HomeUniLoader } from '../../components/HomeUniLoader';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TRIMESTERS = ['Trimester 1', 'Trimester 2', 'Trimester 3'];

// University grading scale
function gradeColor(score) {
  if (score >= 70) return 'text-emerald-600';
  if (score >= 60) return 'text-[#1976D2]';
  if (score >= 50) return 'text-amber-600';
  return 'text-red-600';
}
function gradeBadgeClass(score) {
  if (score >= 70) return 'bg-emerald-100 text-emerald-700';
  if (score >= 60) return 'bg-[#1976D2]/15 text-[#1976D2]';
  if (score >= 50) return 'bg-amber-100 text-amber-700';
  return 'bg-red-100 text-red-700';
}

export default function GradebookPage() {
  const { user } = useAuth();
  const { activeTerm } = useTerm();
  const [tab, setTab] = useState('programme');
  const [programmes, setProgrammes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedProgramme, setSelectedProgramme] = useState('');
  const [selectedTrimester, setSelectedTrimester] = useState(activeTerm || 'Trimester 1');
  const [gradebook, setGradebook] = useState(null);
  const [gradeDialog, setGradeDialog] = useState(false);
  const [gradeForm, setGradeForm] = useState({
    student_id: '', subject_id: '', term: activeTerm || 'Trimester 1',
    ca_score: 0, ca_max: 40, exam_score: 0, exam_max: 60, teacher_comment: ''
  });
  const [reportCard, setReportCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const isTeacher = ['Lecturer', 'Admin', 'SuperAdmin', 'Registrar'].includes(user?.role);

  useEffect(() => {
    setSelectedTrimester(activeTerm);
    setGradeForm(prev => ({ ...prev, term: activeTerm }));
  }, [activeTerm]);

  useEffect(() => {
    Promise.all([
      api.get('/academic/programmes').then(r => setProgrammes(r.data)),
      api.get('/admin/students').then(r => setStudents(r.data)),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleProgrammeSelect = async (progId) => {
    setSelectedProgramme(progId);
    setGradebook(null);
    try {
      const [gbRes, cRes] = await Promise.all([
        api.get(`/gradebook/class/${progId}`, { params: { term: selectedTrimester } }),
        api.get(`/academic/programmes/${progId}/courses`),
      ]);
      setGradebook(gbRes.data);
      setCourses(cRes.data || []);
    } catch { toast.error('Failed to load gradebook'); }
  };

  const handleRecordGrade = async (e) => {
    e.preventDefault();
    try {
      await api.post('/gradebook/entries', {
        ...gradeForm,
        ca_score: parseFloat(gradeForm.ca_score),
        exam_score: parseFloat(gradeForm.exam_score),
        ca_max: parseFloat(gradeForm.ca_max),
        exam_max: parseFloat(gradeForm.exam_max),
      });
      successToast('Grade recorded');
      setGradeDialog(false);
      if (selectedProgramme) handleProgrammeSelect(selectedProgramme);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const viewReportCard = async (studentId) => {
    try {
      const r = await api.get(`/gradebook/report-card/${studentId}`, { params: { term: selectedTrimester } });
      setReportCard(r.data);
      setTab('transcript');
    } catch { toast.error('Failed to load transcript'); }
  };

  const downloadPdf = (studentId) => {
    const token = localStorage.getItem('homeuni_token');
    window.open(`${BACKEND}/api/gradebook/report-card/${studentId}/pdf?term=${selectedTrimester}&token=${token}`, '_blank');
  };

  // Filtered courses for the grade form (by selected programme)
  const progCourses = selectedProgramme
    ? courses
    : programmes.flatMap(p => []);

  if (loading) return <HomeUniLoader />;

  return (
    <div className="space-y-6" data-testid="gradebook-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1565C0]">Gradebook</h1>
          <p className="text-slate-500 mt-1">Student assessments, grades, and academic transcripts</p>
        </div>
        {isTeacher && (
          <Dialog open={gradeDialog} onOpenChange={setGradeDialog}>
            <DialogTrigger asChild>
              <Button className="rounded-md bg-[#2E6CC4]" data-testid="record-grade-btn">
                <Plus className="w-4 h-4 mr-2" /> Record Grade
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Record Student Grade</DialogTitle></DialogHeader>
              <form onSubmit={handleRecordGrade} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Student</Label>
                    <Select value={gradeForm.student_id} onValueChange={v => setGradeForm({...gradeForm, student_id: v})}>
                      <SelectTrigger className="h-9 text-xs" data-testid="grade-student"><SelectValue placeholder="Select student" /></SelectTrigger>
                      <SelectContent>{students.map(s => <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Programme / Course</Label>
                    <Select value={gradeForm.subject_id} onValueChange={v => setGradeForm({...gradeForm, subject_id: v})}>
                      <SelectTrigger className="h-9 text-xs" data-testid="grade-subject"><SelectValue placeholder="Select programme" /></SelectTrigger>
                      <SelectContent>
                        {programmes.map(p => (
                          <SelectItem key={p.programme_id || p.subject_id} value={p.programme_id || p.subject_id}>
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1"><Label className="text-xs">Trimester</Label>
                  <Select value={gradeForm.term} onValueChange={v => setGradeForm({...gradeForm, term: v})}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{TRIMESTERS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="bg-blue-50 rounded-md p-3 text-xs text-blue-700 mb-1">
                  University grading: CA (40%) + Exam (60%) = 100% · Pass mark: 50%
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div className="space-y-1"><Label className="text-xs">CA Score</Label><Input type="number" max="40" value={gradeForm.ca_score} onChange={e => setGradeForm({...gradeForm, ca_score: e.target.value})} className="h-9 text-xs" data-testid="grade-ca" /></div>
                  <div className="space-y-1"><Label className="text-xs">CA Max</Label><Input type="number" value={gradeForm.ca_max} onChange={e => setGradeForm({...gradeForm, ca_max: e.target.value})} className="h-9 text-xs" /></div>
                  <div className="space-y-1"><Label className="text-xs">Exam Score</Label><Input type="number" max="60" value={gradeForm.exam_score} onChange={e => setGradeForm({...gradeForm, exam_score: e.target.value})} className="h-9 text-xs" data-testid="grade-exam" /></div>
                  <div className="space-y-1"><Label className="text-xs">Exam Max</Label><Input type="number" value={gradeForm.exam_max} onChange={e => setGradeForm({...gradeForm, exam_max: e.target.value})} className="h-9 text-xs" /></div>
                </div>
                <div className="space-y-1"><Label className="text-xs">Lecturer Comment</Label>
                  <Input value={gradeForm.teacher_comment} onChange={e => setGradeForm({...gradeForm, teacher_comment: e.target.value})} className="h-9 text-xs" placeholder="e.g. Good analytical skills demonstrated" data-testid="grade-comment" />
                </div>
                <Button type="submit" className="w-full rounded-md bg-[#2E6CC4]" data-testid="submit-grade">Save Grade</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border">
          <TabsTrigger value="programme" className="text-xs">Programme Gradebook</TabsTrigger>
          <TabsTrigger value="transcript" className="text-xs">Academic Transcript</TabsTrigger>
        </TabsList>

        <TabsContent value="programme" className="mt-4 space-y-4">
          <div className="flex gap-3 items-end flex-wrap">
            <div className="space-y-1">
              <Label className="text-xs">Programme</Label>
              <Select value={selectedProgramme} onValueChange={handleProgrammeSelect}>
                <SelectTrigger className="w-64 h-9 text-xs" data-testid="gradebook-class">
                  <SelectValue placeholder="Select programme" />
                </SelectTrigger>
                <SelectContent>
                  {programmes.map(p => (
                    <SelectItem key={p.programme_id || p.subject_id} value={p.programme_id || p.subject_id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Trimester</Label>
              <Select value={selectedTrimester} onValueChange={v => {
                setSelectedTrimester(v);
                if (selectedProgramme) {
                  api.get(`/gradebook/class/${selectedProgramme}`, { params: { term: v } })
                    .then(r => setGradebook(r.data)).catch(() => {});
                }
              }}>
                <SelectTrigger className="w-36 h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{TRIMESTERS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {gradebook ? (
            <div className="bg-white rounded-md border border-slate-100 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="bg-slate-50 sticky left-0 bg-white w-8">#</TableHead>
                    <TableHead className="bg-slate-50 sticky left-8 bg-white min-w-[160px]">Student</TableHead>
                    {gradebook.subjects?.map(s => (
                      <TableHead key={s.subject_id} className="bg-slate-50 text-center min-w-[80px] text-[10px]">
                        {s.code || s.name?.slice(0, 6)}
                      </TableHead>
                    ))}
                    <TableHead className="bg-slate-50 text-center font-bold">GPA</TableHead>
                    <TableHead className="bg-slate-50"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {gradebook.students?.map(row => (
                    <TableRow key={row.student_id} data-testid={`gradebook-row-${row.student_id}`}>
                      <TableCell className="text-xs font-medium sticky left-0 bg-white">{row.position}</TableCell>
                      <TableCell className="font-medium text-sm sticky left-8 bg-white">{row.name}</TableCell>
                      {gradebook.subjects?.map(s => {
                        const g = row.subjects?.[s.subject_id];
                        return (
                          <TableCell key={s.subject_id} className="text-center">
                            {g ? (
                              <div>
                                <span className={`text-sm font-semibold ${gradeColor(g.final)}`}>{g.final}</span>
                                <br/>
                                <span className="text-[10px] text-slate-400">{g.grade}</span>
                              </div>
                            ) : <span className="text-slate-300 text-xs">—</span>}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-center font-bold text-sm">
                        <span className={gradeColor(row.average)}>{row.average}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => viewReportCard(row.student_id)} data-testid={`view-report-${row.student_id}`} title="View transcript">
                            <FileText className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => downloadPdf(row.student_id)} data-testid={`download-pdf-${row.student_id}`} title="Download PDF">
                            <Download className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <BarChart3 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Select a programme to view gradebook</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="transcript" className="mt-4">
          {reportCard ? (
            <Card className="rounded-md border-slate-100 max-w-3xl mx-auto">
              <CardHeader className="bg-[#1565C0] text-white rounded-t-2xl pb-6">
                <CardTitle className="text-center">
                  <p className="text-xl font-bold tracking-wide">HomeUni</p>
                  <p className="text-sm font-normal opacity-70 mt-1">University. Education. Delivered to Your Home</p>
                  <div className="mt-3 border-t border-white/30 pt-3">
                    <p className="text-base font-semibold">Academic Transcript</p>
                    <p className="text-xs font-normal opacity-80">{reportCard.term} · {selectedTrimester}</p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-5">
                <div className="grid grid-cols-2 gap-4 text-sm bg-slate-50 rounded-lg p-4">
                  <div><span className="text-slate-500 text-xs uppercase font-semibold">Student</span><p className="font-semibold mt-0.5">{reportCard.student?.name}</p></div>
                  <div><span className="text-slate-500 text-xs uppercase font-semibold">Admission No.</span><p className="font-semibold mt-0.5">{reportCard.student?.admission_number || '—'}</p></div>
                  <div><span className="text-slate-500 text-xs uppercase font-semibold">Programme</span><p className="font-semibold mt-0.5">{reportCard.class?.name || reportCard.programme?.name || '—'}</p></div>
                  <div><span className="text-slate-500 text-xs uppercase font-semibold">Academic Year</span><p className="font-semibold mt-0.5">{reportCard.student?.academic_year || '2025-2026'}</p></div>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="bg-slate-50">Course / Module</TableHead>
                      <TableHead className="bg-slate-50 text-center">CA (40%)</TableHead>
                      <TableHead className="bg-slate-50 text-center">Exam (60%)</TableHead>
                      <TableHead className="bg-slate-50 text-center">Total</TableHead>
                      <TableHead className="bg-slate-50 text-center">Grade</TableHead>
                      <TableHead className="bg-slate-50">Remark</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportCard.grades?.map((g, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium text-sm">{g.subject}</TableCell>
                        <TableCell className="text-center text-sm">{g.ca_score}/{g.ca_max}</TableCell>
                        <TableCell className="text-center text-sm">{g.exam_score}/{g.exam_max}</TableCell>
                        <TableCell className="text-center font-bold text-sm">
                          <span className={gradeColor(g.final_score)}>{g.final_score}%</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={gradeBadgeClass(g.final_score)}>{g.grade}</Badge>
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">{g.remark}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <div className="flex items-center justify-between bg-[#1565C0]/5 border border-[#1565C0]/20 rounded-lg p-4">
                  <div>
                    <span className="text-sm text-slate-500">Cumulative GPA</span>
                    <p className="text-2xl font-bold text-[#1565C0] mt-0.5">{reportCard.average}%</p>
                  </div>
                  <Badge className={`text-lg px-4 py-2 ${gradeBadgeClass(reportCard.average)}`}>
                    {reportCard.overall_grade} — {reportCard.overall_remark}
                  </Badge>
                </div>

                <div className="bg-slate-50 rounded-lg p-4 text-sm">
                  <p className="font-semibold text-slate-700 mb-2">Grading Scale</p>
                  <div className="grid grid-cols-4 gap-2 text-xs text-slate-500">
                    <span>A: 70–100%</span><span>B+: 65–69%</span><span>B: 60–64%</span><span>C+: 55–59%</span>
                    <span>C: 50–54%</span><span>D: 45–49%</span><span>F: &lt;45%</span>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="font-semibold text-sm text-slate-700 mb-1">Attendance</p>
                  <p className="text-xs text-slate-500">
                    Present: {reportCard.attendance?.present} · Absent: {reportCard.attendance?.absent} · Late: {reportCard.attendance?.late} ·
                    Rate: {reportCard.attendance?.total ? Math.round(reportCard.attendance.present / reportCard.attendance.total * 100) : 0}%
                  </p>
                </div>

                <Button className="w-full rounded-md bg-[#2E6CC4]" onClick={() => downloadPdf(reportCard.student?.user_id)} data-testid="download-report-pdf">
                  <Download className="w-4 h-4 mr-2" /> Download Academic Transcript PDF
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <GraduationCap className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Select a student from the gradebook to view their academic transcript</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
